import React from "react";
import { useParams } from "react-router";
import { Button, Card, Link, LongTextLabel } from "@sscs/ui";
import { AllowedTo } from "Components";
import { ORGANIZATION_CATALOG_ID, PERMISSION } from "Constants";
import { useCatalogRoute } from "Hooks";
import { OrgCatalogReference } from "Types";
import { ExternalPackageSummary } from "../../types";
import CardContent from "../CardContent/CardContent";
import CommonCardTitle from "../CardTitle/CardTitle";
import styles from "./externalSoftwareCard.module.scss";

type CatalogPackageLinkProps = {
  reference: OrgCatalogReference;
  className?: string;
  children: React.ReactNode;
};

const CatalogPackageLink = ({
  reference,
  className,
  children,
}: CatalogPackageLinkProps): React.JSX.Element => {
  const packageRoute = useCatalogRoute({
    catalogId: ORGANIZATION_CATALOG_ID,
    platform: reference.platform,
    packageId: reference.package_id,
  });

  return (
    <Link fullWidth={false} to={packageRoute} className={className}>
      {children}
    </Link>
  );
};

type ExternalSoftwareCardProps = {
  software: ExternalPackageSummary;
  onImport: (software: ExternalPackageSummary) => void;
};

const ExternalSoftwareCard = ({
  software,
  onImport,
}: ExternalSoftwareCardProps): React.JSX.Element => {
  const { groupId } = useParams();

  return (
    <Card.Card className={styles.card}>
      <CommonCardTitle
        fileName={software.product}
        architecture={software.architecture}
      >
        {software.org_catalog_reference ? (
          <CatalogPackageLink reference={software.org_catalog_reference}>
            <LongTextLabel label={software.name} />
          </CatalogPackageLink>
        ) : (
          <LongTextLabel label={software.name} />
        )}
        <LongTextLabel label={software.version} className={styles.version} />
      </CommonCardTitle>
      <CardContent
        softwareInfo={{
          ...software,
          release_date: software.released,
          license: software.license.name,
        }}
        description={software.summary}
        popularityRank={software.popularity}
      />
      {software.org_catalog_reference ? (
        <Card.Actions className={styles.actions}>
          <CatalogPackageLink
            reference={software.org_catalog_reference}
            className={styles.import}
          >
            View in catalog
          </CatalogPackageLink>
        </Card.Actions>
      ) : (
        <AllowedTo permissions={[PERMISSION.CREATE_PACKAGE]} groupId={groupId}>
          <Card.Actions className={styles.actions}>
            <Button
              color="secondary"
              className={styles.import}
              onClick={() => onImport(software)}
            >
              Import package
            </Button>
          </Card.Actions>
        </AllowedTo>
      )}
    </Card.Card>
  );
};

export default ExternalSoftwareCard;
